/**
 * XoeBlockRenderer — maps an array of XoeContentBlock values to the appropriate
 * per-type block component.
 *
 * Block types:
 *   markdown      → MarkdownBlock (pre-wrapped text, no parser)
 *   body_map      → BodyMapBlock  (BodyMapView)
 *   test_results  → TestResultsBlock (lightweight Ant Table)
 *   evidence_refs → EvidenceRefsBlock (EvidenceRefList + useReferenceModal)
 *   literature    → LiteratureBlock (compact citation list with PubMed/DOI/Europe PMC links)
 *   (unknown)     → safe muted fallback — never crashes
 *
 * Props:
 *   blocks    — the content_blocks array from an XoeMessage (never null here;
 *               callers guard before rendering)
 *   patientId — conversation patient_id; null when the conversation is not
 *               patient-scoped (evidence refs render read-only in that case)
 */

import React from 'react';
import { Typography } from 'antd';

import MarkdownBlock from './MarkdownBlock';
import BodyMapBlock from './BodyMapBlock';
import TestResultsBlock from './TestResultsBlock';
import EvidenceRefsBlock from './EvidenceRefsBlock';
import LiteratureBlock from './LiteratureBlock';
import type { BlockProps, XoeBlockRendererProps } from '../../../types/xoe/XoeUiTypes';

const { Text } = Typography;

// ── Unknown-block fallback ────────────────────────────────────────────────────

const UnknownBlock: React.FC<{ type: string }> = ({ type }) => (
	<Text type="secondary" style={{ fontSize: 'var(--mg-fs-xs)', fontStyle: 'italic' }}>
		[Unsupported content block: {type}]
	</Text>
);

// ── Per-block renderer ────────────────────────────────────────────────────────

const Block: React.FC<BlockProps> = ({ block, patientId }) => {
	try {
		switch (block.type) {
			case 'markdown':
				return <MarkdownBlock text={block.text} />;

			case 'body_map':
				return (
					<BodyMapBlock
						tags={block.tags}
						gender={block.gender}
						front_tags={block.front_tags}
						back_tags={block.back_tags}
						anatomical_correlates={block.anatomical_correlates}
						hydrated={block.hydrated}
					/>
				);

			case 'test_results':
				return <TestResultsBlock results={block.results} />;

			case 'evidence_refs':
				return <EvidenceRefsBlock refs={block.refs} patientId={patientId} />;

			case 'literature':
				return <LiteratureBlock items={block.items} />;

			default:
				// TypeScript exhaustiveness: `block` is `never` here; the cast captures
				// runtime blocks with types we haven't modelled yet.
				return <UnknownBlock type={(block as { type: string }).type} />;
		}
	} catch {
		// Last-resort safety net — a malformed block must never crash the thread.
		return (
			<Text type="secondary" style={{ fontSize: 'var(--mg-fs-xs)', fontStyle: 'italic' }}>
				[Failed to render content block]
			</Text>
		);
	}
};

// ── Per-block error boundary ──────────────────────────────────────────────────

// The per-block try/catch above only guards element CONSTRUCTION; a throw during
// a child's render escapes to the app root. This boundary contains a throwing
// child so one bad block renders the fallback instead of crashing the thread.
class BlockBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
	state = { failed: false };
	static getDerivedStateFromError() {
		return { failed: true };
	}
	render() {
		return this.state.failed ? (
			<Text type="secondary" style={{ fontSize: 'var(--mg-fs-xs)', fontStyle: 'italic' }}>
				[Failed to render content block]
			</Text>
		) : (
			this.props.children
		);
	}
}

// ── Public component ──────────────────────────────────────────────────────────

const XoeBlockRenderer: React.FC<XoeBlockRendererProps> = ({ blocks, patientId }) => {
	if (!blocks || blocks.length === 0) return null;

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--mg-sp-5)', marginTop: 'var(--mg-sp-3)' }}>
			{blocks.map((block, idx) => (
				<BlockBoundary key={idx}>
					<Block block={block} patientId={patientId} />
				</BlockBoundary>
			))}
		</div>
	);
};

export default XoeBlockRenderer;
